import React, {ReactElement} from "react";
import {InView} from "react-intersection-observer";

import Image, {ImageObject} from "../image/Image";
import Video, {VideoType} from "../videos/video/Video";
import CTAButton, {CTA} from "../buttons/cta-button/CTAButton";
import SimpleFormWrapper from "../forms/simple-form-wrapper/SimpleFormWrapper";
import ModuleBody from "../layouts/module-body/ModuleBody";

import {css, generateKeyId} from "/src/utils/utils";
import {LOCALE} from "/src/config";
import {visibleForLocale} from "/src/utils/utils";

import * as styles from "./scroll-takeover-module.module.scss";
import {SimpleFormProps} from "/src/types";

const ScrollTakeoverModule = (
  props: Omit<Queries.ScrollTakeoverModuleFragmentFragment, "__typename">
): ReactElement | null => {
  const {mediaSpaceAllocation, imageLocation, scrollTakeoverSlides, visibleIn} =
    props;

  const [selectedSlideIndex, setSelectedSlideIndex] = React.useState<number>(0);

  if (
    !visibleForLocale(visibleIn as Array<string>, LOCALE) ||
    !scrollTakeoverSlides ||
    !scrollTakeoverSlides.length
  ) {
    return null;
  }

  const addAnimation = (index: number, inView: boolean) => {
    if (inView) {
      setSelectedSlideIndex(index);
    }
  };

  return (
    <section
      className={css(
        styles.scrollTakeoverModule,
        imageLocation === "left" ? styles.scrollTakeoverModuleLeft : "",
        mediaSpaceAllocation === "75%" ? styles.scrollTakeoverModule75 : ""
      )}
    >
      <ModuleBody classes={styles.scrollTakeoverModule__body}>
        <div className={styles.scrollTakeoverModule__content}>
          {scrollTakeoverSlides.map((slide, i) => {
            const form: SimpleFormProps | null =
              mediaSpaceAllocation === "50%" && slide?.form
                ? (slide.form as SimpleFormProps)
                : null;

            const key = [
              "scroll-takeover-content",
              slide?.eyebrow || "",
              slide?.title || "",
              i,
            ].join("-");

            return (
              <InView
                as="div"
                threshold={0.5}
                key={generateKeyId(key)}
                onChange={inView => addAnimation(i, inView)}
              >
                <div className={styles.scrollTakeoverModule__slide}>
                  {slide?.eyebrow && (
                    <p
                      className={css(
                        "eyebrow",
                        styles.scrollTakeoverModule__slide__eyebrow
                      )}
                    >
                      {slide.eyebrow}
                    </p>
                  )}

                  {slide?.title && (
                    <h3
                      className={css(
                        "h3",
                        styles.scrollTakeoverModule__slide__title
                      )}
                    >
                      {slide.title}
                    </h3>
                  )}

                  {slide?.body?.body && (
                    <p
                      className={css(
                        "caption",
                        styles.scrollTakeoverModule__slide__caption
                      )}
                    >
                      {slide.body.body}
                    </p>
                  )}

                  <div className={styles.scrollTakeoverModule__slide__ctas}>
                    {slide?.ctaButton && (
                      <CTAButton
                        cta={slide.ctaButton as CTA}
                        kind="secondary"
                        classes={
                          styles.scrollTakeoverModule__slide__ctas__ctaPrimary
                        }
                      />
                    )}

                    {slide?.secondaryCtaButton && (
                      <CTAButton
                        cta={slide.secondaryCtaButton as CTA}
                        kind="tertiary"
                      />
                    )}
                  </div>

                  {form && (
                    <SimpleFormWrapper
                      {...form}
                      classes={styles.scrollTakeoverModule__slide__form}
                    />
                  )}
                </div>
              </InView>
            );
          })}
        </div>
        <div
          className={css(
            styles.scrollTakeoverModule__contentMedia,
            styles.scrollTakeoverModule__media
          )}
        >
          {scrollTakeoverSlides.map((slide, i) => {
            const key = [
              "scroll-takeover-media",
              slide?.eyebrow || "",
              slide?.title || "",
              i,
            ].join("-");

            const videoBehavior = (
              slide?.videoBehavior ? slide.videoBehavior : "autoplay"
            ) as VideoType;

            return (
              <div
                className={css(
                  styles.scrollTakeoverModule__media__content,
                  selectedSlideIndex === i
                    ? styles.scrollTakeoverModule__media__contentLoaded
                    : styles.scrollTakeoverModule__media__contentLeave
                )}
                key={generateKeyId(key)}
              >
                {slide?.videoSource && (
                  <Video
                    videoTitle={slide.title!}
                    videoClasses={styles.scrollTakeoverModule__media__video}
                    videoSource={slide.videoSource}
                    showControl={videoBehavior !== "autoplay"}
                    type={videoBehavior}
                  />
                )}

                {!slide?.videoSource && slide?.image && (
                  <Image
                    image={slide.image as ImageObject}
                    classes={styles.scrollTakeoverModule__media__img}
                  />
                )}
              </div>
            );
          })}
        </div>
      </ModuleBody>
    </section>
  );
};

export default ScrollTakeoverModule;
